// Decides how text from an MCP server is shown. A resource item carries a mime
// type and is presented by it; a plain text item has to be sniffed, since
// servers hand back JSON documents and header-less pipe tables as prose, and
// both read badly through the markdown renderer as-is.

export type McpJsonPrimitive = string | number | boolean | null;

export type McpTextPresentation =
  | { kind: "json"; pretty: string }
  | { kind: "table"; header: string[]; rows: McpJsonPrimitive[][] }
  | { kind: "markdown"; markdown: string }
  | { kind: "pre"; text: string };

const MAX_TABLE_ROWS = 200;
const MAX_TABLE_COLUMNS = 12;

const NUMERIC = /^[+-]?[$€£¥]?\d[\d,]*(\.\d+)?%?$/;

// Right-aligned in a table: a JSON number, or a CSV cell that reads as one.
export const isNumericCell = (cell: McpJsonPrimitive): boolean =>
  typeof cell === "number" || (typeof cell === "string" && NUMERIC.test(cell));

const isPrimitive = (v: unknown): v is McpJsonPrimitive =>
  v === null || typeof v === "string" || typeof v === "number" || typeof v === "boolean";

const isPlainObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

// Rows of flat objects become a table over the union of their keys, in
// first-seen order; a key an object lacks is an empty cell, since servers
// commonly omit null fields.
const tabulate = (value: unknown): McpTextPresentation | null => {
  if (!Array.isArray(value) || value.length === 0 || value.length > MAX_TABLE_ROWS) return null;
  if (!value.every(isPlainObject)) return null;
  const header = [...new Set(value.flatMap(Object.keys))];
  if (header.length === 0 || header.length > MAX_TABLE_COLUMNS) return null;
  const rows = value.map((item) => header.map((key) => item[key] ?? null));
  if (!rows.every((row): row is McpJsonPrimitive[] => row.every(isPrimitive))) return null;
  return { kind: "table", header, rows };
};

const UNESCAPED_PIPE = /(?<!\\)\|/;

// Cells of a line that looks like a table row: at least one unescaped pipe and
// two cells once optional outer pipes are dropped. Escapes stay in place so the
// markdown parser still sees them.
const pipeCells = (line: string): string[] | null => {
  const cells = line.split(UNESCAPED_PIPE).map((cell) => cell.trim());
  if (cells.length < 2) return null;
  if (cells[0] === "") cells.shift();
  if (cells.at(-1) === "") cells.pop();
  return cells.length >= 2 ? cells : null;
};

const isSeparatorCell = (cell: string) => /^:?-+:?$/.test(cell);

const FENCE = /^\s*(```|~~~)/;

const isHeaderCell = (cell: string) =>
  cell !== "" && cell.length <= 40 && cell.split(/\s+/).length <= 4 && !/^[$\->#]/.test(cell);

// A number (thousands separators, sign, currency, percent), an ISO-ish date or
// a clock time prefix, a boolean word, or nothing at all.
const isTypedCell = (cell: string) =>
  cell === "" ||
  NUMERIC.test(cell) ||
  /^(\d{4}-\d{2}-\d{2}|\d{1,2}:\d{2})/.test(cell) ||
  /^(true|false|yes|no)$/i.test(cell);

// Shell pipelines, CLI usage and prose with a stray pipe also come in runs of
// equal-width pipe lines, so a run needs the shape of data before it is
// rewritten: enough rows, label-like header cells, no code, and one column of
// typed values. A miss renders as the plain lines it always did; a false
// positive mangles prose, so evidence is required.
const looksLikeTable = (rows: string[][]): boolean => {
  const [header, ...body] = rows;
  if (!header || header.length < 2) return false;
  if (rows.length < (header.length >= 4 ? 2 : 3)) return false;
  if (!header.every(isHeaderCell)) return false;
  if (rows.some((cells) => cells.some((cell) => cell.includes("`")))) return false;
  return header.some((_, col) => body.every((cells) => isTypedCell(cells[col] ?? "")));
};

// Runs of consecutive pipe rows with the same cell count that pass
// `looksLikeTable` get the GFM dressing (outer pipes and a separator after the
// first row). Text that already carries a separator row is a real table and is
// left alone, as is anything inside a code fence.
const normalisePipeTables = (text: string): string => {
  let inFence = false;
  const lines = text.split("\n").map((line) => {
    if (FENCE.test(line)) inFence = !inFence;
    return { line, cells: inFence ? null : pipeCells(line) };
  });
  if (lines.some(({ cells }) => cells?.every(isSeparatorCell))) return text;

  const out: string[] = [];
  for (let start = 0; start < lines.length;) {
    const width = lines[start]?.cells?.length;
    let end = start + 1;
    if (width !== undefined) {
      while (lines[end]?.cells?.length === width) end++;
    }
    const run = lines.slice(start, end);
    const rows = run.map(({ cells }) => cells ?? []);
    if (looksLikeTable(rows)) {
      rows.forEach((cells, idx) => {
        out.push(`| ${cells.join(" | ")} |`);
        if (idx === 0) out.push(`| ${cells.map(() => "---").join(" | ")} |`);
      });
    } else {
      out.push(...run.map(({ line }) => line));
    }
    start = end;
  }
  return out.join("\n");
};

const presentJson = (text: string): McpTextPresentation | null => {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  return tabulate(value) ?? { kind: "json", pretty: JSON.stringify(value, null, 2) };
};

// RFC 4180 records: quoted fields may hold commas, newlines and doubled
// quotes; rows end at LF or CRLF. Blank lines are dropped.
const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charAt(i);
    if (quoted) {
      if (ch !== '"') cell += ch;
      else if (text.charAt(i + 1) === '"') {
        cell += '"';
        i++;
      } else quoted = false;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text.charAt(i + 1) === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length > 0) rows.push([...row, cell]);
  return rows.filter((cells) => cells.some((c) => c !== ""));
};

// A resource body is presented by its mime type, with no sniffing: the server
// said what it is. Anything that is not JSON, CSV or markdown is shown raw.
export const presentMcpResourceText = (mimeType: string, text: string): McpTextPresentation => {
  const mime = mimeType.split(";")[0]?.trim().toLowerCase() ?? "";
  if (mime === "application/json" || mime.endsWith("+json")) return presentJson(text) ?? { kind: "pre", text };
  if (mime === "text/csv") {
    const [header, ...body] = parseCsv(text);
    if (!header) return { kind: "pre", text };
    // A ragged row is padded to the header, and extra cells are dropped.
    const rows = body.slice(0, MAX_TABLE_ROWS).map((row) => header.map((_, col) => row[col] ?? ""));
    return { kind: "table", header, rows };
  }
  if (mime === "text/markdown") return { kind: "markdown", markdown: text };
  return { kind: "pre", text };
};

export const presentMcpText = (text: string): McpTextPresentation => {
  const trimmed = text.trim();
  const json = trimmed.startsWith("{") || trimmed.startsWith("[") ? presentJson(trimmed) : null;
  return json ?? { kind: "markdown", markdown: normalisePipeTables(text) };
};
